import { type AdminRole, type Me, MeResponseSchema, type Need, SessionResponseSchema } from '@oneapp/shared';
import { useQueryClient } from '@tanstack/react-query';
import type { ConfirmationResult } from 'firebase/auth';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { ApiError, api, isSignInRefused, setAppCheckSource, setTokenSource } from '../lib/api';
import {
  appCheckToken,
  confirmSignInCode,
  currentIdToken,
  isSignInConfigured,
  signOutOfFirebase,
  watchSignIn,
} from '../lib/firebase';
import { applyLanguage } from '../lib/i18n';
import { clearReturnTo } from './returnTo';
import { signInFlow } from './signInFlow';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut' }
  /** Sign-in isn't configured on this build (no Firebase settings). */
  | { status: 'unavailable' }
  /** Signed in, but the account couldn't be loaded: offline, or the server had a problem. */
  | { status: 'error'; reason: 'offline' | 'server'; retry: () => void }
  /** `roles`: their admin roles (gap 2), for Menu → Admin. */
  | { status: 'signedIn'; me: Me; needs: Need[]; isNew: boolean; roles: AdminRole[] };

/** Dispatched when the server refuses the sign-in (revoked, blocked…): the app signs out. */
export const SIGN_IN_REFUSED_EVENT = 'oneapp:sign-in-refused';

interface AuthContextValue {
  state: AuthState;
  /** Confirm the SMS code, then start the EkApp session (which creates the account the first time). */
  confirmCode(confirmation: ConfirmationResult, code: string): Promise<void>;
  /** New account details after a change. `firstVisit`: they've just finished creating the account. */
  update(me: Me, needs: Need[], options?: { firstVisit?: boolean }): void;
  /** Sign out on every device. */
  signOut(): Promise<void>;
  /** Sign out on this phone only: the server has already signed the person out (deleting the account). */
  signOutHere(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth needs <AuthProvider>');
  return value;
}

/** The sign-in when there is one, for pages that also work without the app around them (the Keys page). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}

/** The signed-in person, for screens behind RequireSignedIn. */
export function useSignedIn(): Extract<AuthState, { status: 'signedIn' }> {
  const { state } = useAuth();
  if (state.status !== 'signedIn') throw new Error('useSignedIn outside a signed-in screen');
  return state;
}

const reasonFor = (error: unknown): 'offline' | 'server' =>
  error instanceof ApiError && error.status !== 0 ? 'server' : 'offline';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>(() =>
    isSignInConfigured() ? { status: 'loading' } : { status: 'unavailable' },
  );
  const [attempt, setAttempt] = useState(0);
  // While a code is being confirmed, confirmCode does the work the sign-in watcher would otherwise do.
  const confirming = useRef(false);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const signedIn = useCallback((me: Me, needs: Need[], isNew: boolean, roles: AdminRole[]) => {
    void applyLanguage(me.chatLanguage);
    setState({ status: 'signedIn', me, needs, isNew, roles });
  }, []);

  const signOutLocally = useCallback(async () => {
    signInFlow.clear();
    clearReturnTo();
    queryClient.clear();
    await signOutOfFirebase().catch(() => {});
    setState({ status: 'signedOut' });
  }, [queryClient]);

  /** A sign-in Firebase remembered from an earlier visit: load the account, finishing it if needed. */
  const fetchAccount = useCallback(async () => {
    try {
      const { user, needs, roles } = await api.get('/api/v1/me', MeResponseSchema, { auth: true });
      signedIn(user, needs, false, roles);
      return;
    } catch (error) {
      const noAccountYet = error instanceof ApiError && error.problem?.code === 'TOKEN_INVALID';
      if (!noAccountYet) {
        if (isSignInRefused(error)) await signOutLocally();
        else setState({ status: 'error', reason: reasonFor(error), retry });
        return;
      }
    }
    try {
      const session = await api.post('/api/v1/auth/session', SessionResponseSchema, { auth: true });
      signedIn(session.user, session.needs, session.isNew, session.roles);
    } catch (error) {
      if (isSignInRefused(error) || (error instanceof ApiError && error.status === 403)) await signOutLocally();
      else setState({ status: 'error', reason: reasonFor(error), retry });
    }
  }, [retry, signOutLocally, signedIn]);

  // One load at a time: the sign-in watcher can fire more than once while the app starts.
  const inFlight = useRef<Promise<void> | null>(null);
  const loadAccount = useCallback(() => {
    inFlight.current ??= fetchAccount().finally(() => {
      inFlight.current = null;
    });
    return inFlight.current;
  }, [fetchAccount]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is how "Try again" re-runs this.
  useEffect(() => {
    if (!isSignInConfigured()) return;
    setTokenSource(currentIdToken);
    setAppCheckSource(appCheckToken);
    let stop: (() => void) | undefined;
    let cancelled = false;
    watchSignIn((user) => {
      if (confirming.current) return;
      if (user) void loadAccount();
      else setState({ status: 'signedOut' });
    }).then(
      (unsubscribe) => {
        if (cancelled) unsubscribe();
        else stop = unsubscribe;
      },
      () => setState({ status: 'error', reason: 'offline', retry }),
    );
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [loadAccount, retry, attempt]);

  useEffect(() => {
    const onRefused = () => void signOutLocally();
    window.addEventListener(SIGN_IN_REFUSED_EVENT, onRefused);
    return () => window.removeEventListener(SIGN_IN_REFUSED_EVENT, onRefused);
  }, [signOutLocally]);

  const confirmCode = useCallback(
    async (confirmation: ConfirmationResult, code: string) => {
      confirming.current = true;
      try {
        // Throws a SignInError (wrong code, expired…) and leaves the person signed out.
        await confirmSignInCode(confirmation, code);
        try {
          const session = await api.post('/api/v1/auth/session', SessionResponseSchema, { auth: true });
          signInFlow.clear();
          signedIn(session.user, session.needs, session.isNew, session.roles);
        } catch (error) {
          signInFlow.clear();
          if (isSignInRefused(error) || (error instanceof ApiError && error.status === 403)) {
            await signOutLocally();
            throw error;
          }
          // The code was right, but EkApp couldn't be reached: "Try again" finishes the account step.
          setState({ status: 'error', reason: reasonFor(error), retry });
        }
      } finally {
        confirming.current = false;
      }
    },
    [retry, signOutLocally, signedIn],
  );

  const update = useCallback((me: Me, needs: Need[], options: { firstVisit?: boolean } = {}) => {
    void applyLanguage(me.chatLanguage);
    setState((previous) => ({
      status: 'signedIn',
      me,
      needs,
      isNew: Boolean(options.firstVisit) || (previous.status === 'signedIn' && previous.isNew),
      roles: previous.status === 'signedIn' ? previous.roles : [],
    }));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout', z.null(), { auth: true });
    } catch {
      // Offline: still sign out on this phone.
    }
    await signOutLocally();
  }, [signOutLocally]);

  const value = useMemo(
    () => ({ state, confirmCode, update, signOut, signOutHere: signOutLocally }),
    [state, confirmCode, update, signOut, signOutLocally],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
